import GuideLayout from "./GuideLayout";
import { TransparencyFigure } from "./GuideFigures";

export default function ConvertEpsToJpg() {
  return (
    <GuideLayout
      title="How to Convert EPS to JPG"
      description="Convert EPS to JPG for small, web-friendly images. Learn what resolution to pick, why your JPG gets a white background, when JPG ruins a logo, and how to convert online or from the command line."
      path="/guides/convert-eps-to-jpg"
      updated="August 1, 2026"
      related={["/guides/convert-eps-to-png", "/guides/vector-vs-raster", "/guides/convert-eps-to-pdf"]}
    >
      <p className="lead">
        JPG is the most widely supported image format on the web. Converting an EPS to
        JPG gives you a compact, universal image that opens anywhere — but the
        conversion throws away things the EPS was carrying, and JPG is a genuinely bad
        choice for some artwork. Here is how to do it properly, and how to tell when you
        should reach for PNG or PDF instead.
      </p>

      <h2>What actually happens during the conversion</h2>
      <p>
        An EPS file is <a href="/guides/vector-vs-raster">vector</a> data: a set of
        PostScript instructions describing shapes, curves and fills as mathematics. It
        has no inherent resolution — the same file can print on a business card or a
        billboard.
      </p>
      <p>
        JPG is a raster format: a fixed grid of pixels. So converting EPS to JPG means{" "}
        <strong>rasterizing</strong> — a renderer executes the PostScript, draws it at
        one specific pixel size, and freezes the result. Three things happen at that
        moment, and all three are permanent:
      </p>
      <ul>
        <li>
          <strong>Resolution is baked in.</strong> Enlarging the JPG afterwards just
          blurs it. You cannot get the vector precision back.
        </li>
        <li>
          <strong>Transparency is lost.</strong> JPG has no alpha channel at all, so any
          transparent area is filled with a solid colour — almost always white.
        </li>
        <li>
          <strong>Colour is converted to RGB.</strong> Print-oriented EPS files are
          usually CMYK, and the shift is visible.
        </li>
      </ul>
      <p>
        None of that is a bug — it is what JPG is. Always keep the original{" "}
        <code>.eps</code>, because it is the only copy that retains the full-quality
        artwork.
      </p>

      <h2>How to convert EPS to JPG online</h2>
      <ol>
        <li>Open the <a href="/">EPS Viewer</a> and upload your <code>.eps</code> file (or click <strong>Try a sample</strong>).</li>
        <li>Wait a few seconds for the preview to render.</li>
        <li>Click the <strong>JPG</strong> button below the preview to download your image.</li>
      </ol>
      <p>
        Nothing is installed and the rendering happens on our servers, so it works
        identically on Windows, macOS, Linux, Android and iOS. If the preview itself
        fails to appear, the file is likely damaged or unusually complex — see{" "}
        <a href="/guides/fix-eps-file-problems">common EPS problems</a>.
      </p>

      <h2>Pick the size before you convert, not after</h2>
      <p>
        Because resolution is fixed at conversion time, decide where the image will be
        used first. Converting once at the right size beats converting small and
        enlarging later, which never works.
      </p>
      <div className="table-wrap">
        <table className="tbl">
          <thead><tr><th>Where it will be used</th><th>Sensible width</th><th>Resolution</th></tr></thead>
          <tbody>
            <tr><td>Email signature / small web logo</td><td>300–600 px</td><td>72–96 DPI</td></tr>
            <tr><td>Website banner or hero image</td><td>1600–2400 px</td><td>96 DPI</td></tr>
            <tr><td>Presentation slide</td><td>1920 px</td><td>96 DPI</td></tr>
            <tr><td>Printed flyer or brochure</td><td>Size in inches × 300</td><td>300 DPI</td></tr>
            <tr><td>Large-format poster or banner</td><td>Use the EPS or a PDF</td><td>—</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        For anything going to a professional printer, do not send a JPG at all. Send the
        EPS itself or a <a href="/guides/convert-eps-to-pdf">print-ready PDF</a>, both of
        which keep the vectors sharp at any size.
      </p>

      <h2>Why your JPG has a white background</h2>
      <p>
        This is the single most common surprise. JPG has no alpha channel, so
        transparent regions must become <em>some</em> colour, and renderers default to
        white. A logo designed to sit on a coloured page will therefore arrive as
        artwork inside a white rectangle.
      </p>
      <TransparencyFigure />
      <p>
        There is no JPG setting that fixes this — the format simply cannot store
        transparency. Your options are:
      </p>
      <ul>
        <li>
          <strong>Use <a href="/guides/convert-eps-to-png">PNG</a> instead.</strong> PNG
          supports full alpha transparency and is the correct choice for logos.
        </li>
        <li>
          <strong>Match the background deliberately.</strong> If the final page has a
          known solid colour, place the artwork on that colour in a vector editor before
          exporting, so the edges blend instead of showing a white box.
        </li>
      </ul>
      <p>
        Worth knowing: EPS does not support live transparency either. Effects like drop
        shadows and glows are already flattened when the EPS is written, which is why
        soft shadows sometimes show a faint rectangular edge.
      </p>

      <h2>When JPG is the wrong format</h2>
      <p>
        JPG compresses by splitting the image into 8×8 pixel blocks and discarding
        high-frequency detail within each block. High-frequency detail is exactly what a
        hard edge is. That is why JPG is excellent for photographs — smooth gradients,
        few hard edges — and poor for the artwork EPS files usually contain.
      </p>
      <p>Expect visible damage when the artwork has:</p>
      <ul>
        <li><strong>Sharp edges between flat colours</strong> — you get faint halos, called ringing or mosquito noise.</li>
        <li><strong>Small text</strong> — letterforms smear and thin strokes break up.</li>
        <li><strong>Fine line work</strong> — hairlines fade or turn grey.</li>
        <li><strong>Large flat areas of one colour</strong> — subtle blotches appear where the colour should be perfectly even.</li>
      </ul>
      <p>
        Since EPS is used overwhelmingly for logos, icons and line illustrations, PNG is
        the better default for most EPS conversions. Choose JPG when the EPS contains an
        embedded photograph, or when file size genuinely matters more than crisp edges.
      </p>

      <h2>Colours look duller than the original</h2>
      <p>
        If the EPS was prepared for print it is almost certainly in CMYK. Your screen is
        RGB, and RGB can display colours that CMYK ink cannot reproduce. Converting CMYK
        to RGB therefore shifts the colours — saturated oranges, deep blues and greens
        typically flatten out the most.
      </p>
      <p>
        This is normal and does not mean the file is broken. If precise colour matters,
        ask whoever supplied the artwork for an RGB version, or open the EPS in Inkscape
        or Illustrator and convert the document colour mode to RGB yourself before
        exporting. Spot colours such as Pantone inks have no exact RGB equivalent at all
        and will always be approximated.
      </p>

      <h2>Quality settings that are worth using</h2>
      <ul>
        <li><strong>Quality 85–95%.</strong> Below about 80% artifacts become obvious on artwork; above 95% the file grows quickly for almost no visible gain.</li>
        <li><strong>Render larger, then scale down.</strong> Rendering at roughly twice the final width and downscaling averages away much of the block noise.</li>
        <li><strong>Never re-save a JPG repeatedly.</strong> JPG is lossy, so every save compounds the damage. Always re-export from the original EPS instead.</li>
        <li><strong>Use progressive JPG for the web.</strong> Same quality, slightly smaller, and it renders sooner on slow connections.</li>
      </ul>

      <h2>Other ways to convert</h2>
      <p>If you would rather work locally, or need to convert many files at once:</p>
      <ul>
        <li><strong>Inkscape (free):</strong> File → Import, then File → Export As, choose the size in pixels, and export to PNG — then convert to JPG if you specifically need it.</li>
        <li><strong>GIMP (free):</strong> opens EPS via Ghostscript and asks for the resolution on import. Set that before clicking OK, then File → Export As → <code>.jpg</code>.</li>
        <li><strong>Adobe Illustrator or Photoshop:</strong> File → Export → Export As → JPEG, with full control over quality and colour profile.</li>
        <li>
          <strong>Command line (ImageMagick with Ghostscript):</strong> useful for
          batches — <code>magick -density 300 input.eps -background white -flatten -quality 90 output.jpg</code>.
          The <code>-density</code> flag must come before the input file, or it is ignored
          and you get a low-resolution result.
        </li>
      </ul>
      <p>
        All local routes need Ghostscript installed to interpret PostScript, which is the
        usual reason a desktop app opens an EPS as a blank or grey box.
      </p>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Can a JPG have a transparent background?</dt>
        <dd>No. The JPG format has no alpha channel, so transparency is always replaced with a solid colour. Use PNG if you need transparency.</dd>
        <dt>Is JPG good for logos?</dt>
        <dd>Usually not. JPG compression blurs the sharp edges and text that logos are made of. Use PNG for screen use, or the original EPS or a PDF for print.</dd>
        <dt>Why is my converted JPG blurry?</dt>
        <dd>Either it was rendered at too low a resolution for the size you are displaying it at, or you enlarged it after conversion. Re-convert from the EPS at the final pixel size you need.</dd>
        <dt>Does converting to JPG reduce quality even at 100%?</dt>
        <dd>Yes. JPG is lossy at every quality setting, and rasterizing discards the vector data regardless. Keep the EPS as your master copy.</dd>
        <dt>Can I convert JPG back to EPS?</dt>
        <dd>Not meaningfully. You can wrap the pixels inside an EPS container, but the artwork stays raster. Recovering true vectors requires redrawing or auto-tracing, which only approximates the original.</dd>
        <dt>Is it safe to convert confidential artwork online?</dt>
        <dd>Files here are transferred over HTTPS and removed after your session. For artwork under a strict NDA, a local tool such as Inkscape keeps the file on your own machine.</dd>
      </dl>
    </GuideLayout>
  );
}
